import {
    inject,
    Injectable,
    signal,
    ViewContainerRef,
    WritableSignal,
} from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Image } from '../components/apartment/header/header.component';
import { TranslateService } from '@ngx-translate/core';
import { DOMHandlerService } from './domhandler.service';

@Injectable({
    providedIn: 'root',
})
export class RendererService {
    viewContainerRef: WritableSignal<ViewContainerRef | undefined> =
        signal(undefined);
    private titleService = inject(Title);
    private metaService = inject(Meta);
    private translationService = inject(TranslateService);
    private domHandler = inject(DOMHandlerService);
    async loadComponents(
        response: GraphQLPage,
        componentMap: ComponentMap,
        slug: string,
        secondarySlug: string | undefined,
    ) {
        if (
            !response ||
            response.data.pages.length === 0 ||
            response.data.pages[0].components.length === 0 ||
            !this.viewContainerRef()
        ) {
            this.viewContainerRef()?.clear();
            this.translationService
                .get('notFound.notFound')
                .subscribe((res) => {
                    this.handleSEO({ components: [] }, res, undefined);
                });
            this.addComponents(
                [{ __component: 'ComponentMainNotFound' }],
                componentMap,
            );
            return;
        }
        const attributes = response.data.pages[0];
        if (!attributes) {
            this.viewContainerRef()?.clear();
            return;
        }
        this.viewContainerRef()?.clear();
        this.handleSEO(attributes, slug, secondarySlug);
        await this.addComponents(attributes.components, componentMap);
    }

    private async addComponents(
        components: BaseComponent[],
        componentMap: any,
    ) {
        if (
            !this.isViewContainerValid() ||
            !this.viewContainerRef()?.injector
        ) {
            console.warn(
                'View container is no longer valid. Skipping component creation.',
            );
            return;
        }

        for (const componentData of components) {
            if (!componentData.__component) {
                continue;
            }

            const componentLoader = componentMap[componentData.__component];
            if (componentLoader) {
                try {
                    const component =
                        typeof componentLoader === 'function'
                            ? await componentLoader()
                            : await componentLoader;

                    const componentRef =
                        this.viewContainerRef()!.createComponent(
                            component,
                        ) as any;
                    componentRef.instance.data = signal(componentData);
                } catch (error) {}
            }
        }
    }

    private isViewContainerValid(): boolean {
        try {
            this.viewContainerRef()?.element;
            return true;
        } catch (error) {
            return false;
        }
    }

    private handleSEO(
        componentWithSEO: ComponentWithSEO,
        slug: string,
        secondarySlug: string | undefined,
    ) {
        this.handleTitle(slug, secondarySlug);
        if (componentWithSEO.seo) {
            this.handleMetaTags(componentWithSEO.seo);
            if (componentWithSEO.seo?.metaTitle) {
                this.handleTitle(componentWithSEO.seo.metaTitle, secondarySlug);
            }
        }
    }

    private handleTitle(slug: string, secondarySlug: string | undefined) {
        if (!slug) {
            return;
        }

        this.titleService.setTitle(
            `Zehentmoar - ${this.makeTitle(slug, secondarySlug)}`,
        );
    }

    private handleMetaTags(seo: SEO) {
        if (seo.metaDescription) {
            this.metaService.updateTag({
                name: 'description',
                content: seo.metaDescription,
            });
        }
        if (seo.metaKeywords) {
            this.metaService.updateTag({
                name: 'keywords',
                content: seo.metaKeywords,
            });
        }
        if (seo.metaRobots) {
            this.metaService.updateTag({
                name: 'robots',
                content: seo.metaRobots,
            });
        }
        if (seo.canonicalURL) {
            this.metaService.updateTag({
                rel: 'canonical',
                href: seo.canonicalURL,
            });
        }
        if (seo.structuredData) {
            this.domHandler.addStructuredData(seo.structuredData);
        }

        if (seo.facebook) {
            if (seo.facebook.title) {
                this.metaService.updateTag({
                    property: 'og:title',
                    content: seo.facebook.title,
                });
            }
            if (seo.facebook.description) {
                this.metaService.updateTag({
                    property: 'og:description',
                    content: seo.facebook.description,
                });
            }
            if (seo.facebook.image) {
                this.metaService.updateTag({
                    property: 'og:image',
                    content: this.domHandler.getLargestImage(
                        seo.facebook.image,
                    ),
                });
            }
        }

        if (seo.twitter) {
            if (seo.twitter.title) {
                this.metaService.updateTag({
                    name: 'twitter:title',
                    content: seo.twitter.title,
                });
            }
            if (seo.twitter.description) {
                this.metaService.updateTag({
                    name: 'twitter:description',
                    content: seo.twitter.description,
                });
            }
            if (seo.twitter.image) {
                this.metaService.updateTag({
                    name: 'twitter:image',
                    content: this.domHandler.getLargestImage(seo.twitter.image),
                });
            }
        }
    }

    private makeTitle(slug: string, secondarySlug: string | undefined): string {
        const words = slug.split('-');
        if (secondarySlug) {
            const secondaryWords = secondarySlug.split('-');
            return this.capitalizeWords(secondaryWords);
        }

        return this.capitalizeWords(words);
    }

    private capitalizeWords(words: string[]): string {
        return words
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }
}

export type GraphQLPage = {
    data: {
        pages: Array<
            {
                slug?: string;
                title?: string;
            } & ComponentWithSEO
        >;
    };
};

export type Page = {
    data: ComponentWithSEO[];
};

type ComponentWithSEO = {
    components: BaseComponent[];
    seo?: SEO;
};

export type SEO = {
    metaTitle?: string;
    metaDescription?: string;
    metaKeywords?: string;
    canonicalURL?: string;
    metaRobots?:
        | 'index, follow'
        | 'noindex, follow'
        | 'index, nofollow'
        | 'noindex, nofollow';
    structuredData?: {};
    facebook?: SocialSEO;
    twitter?: SocialSEO;
};

export type SocialSEO = {
    title?: string;
    description?: string;
    image?: Image;
};

type BaseComponent = {
    __component: string;
    [key: string]: any;
};

export type ComponentMap = {
    [key: string]: () => Promise<any> | Promise<any>;
};

export type MetaTag = {
    name?: string;
    property?: string;
    content: string;
};
